import axios from 'axios';
import { getToken } from './Cookie';
import { endSession } from './Session';
import { unstable_cache } from 'next/cache';
import crypto from 'crypto';
import { HTTP_REQUEST, REVALIDATE_TAG_ALL } from './Constants';
import { GenericResponse } from '@/models/GenericResponse';
import { getCookieForApp, getOsType, getTokenForApp, setTokenForApp } from './CookieUtils';
import { createAnonymousSession } from './anonymousSession';
import { saveExperiments } from './abExperiments';
import { buildAppContextHeaders } from './AppRequestHeaders';
import { setIsAnonymous, setLocalToken, setLocalUser } from './LocalStorage';
import { clearOrderFeedbackFlowStorage } from './OrderFeedbackStorage';
import { appRPC, checkRPC } from './utils';
import { clearFavorites } from './FavoritesStorage';
import { getApiBaseUrl } from './apiBaseUrl';
import { reportApiFailureFromRequester } from './reportOpsSignal';

// Lowered from 20s: a slow upstream used to hold a Node worker for up to 20s per
// SSR request. Since the server is single-threaded, enough concurrent slow renders
// saturate the event loop and even /api/health times out (ALB marks the task
// unhealthy -> restart cascade). Tunable via env without a code change; keep it
// comfortably above the p99 of payment/checkout upstream calls.
const API_TIMEOUT_MS = Number(process.env.API_TIMEOUT_MS) || 8000;

export const Requester = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: API_TIMEOUT_MS,
});

Requester.interceptors.request.use(
  async (config) => {
    // Resolve per-request so ECS runtime INTERNAL_API_URL is honored on the server
    // without changing browser calls (getApiBaseUrl keeps NEXT_PUBLIC_* in the browser).
    config.baseURL = getApiBaseUrl();

    const appContextHeaders = buildAppContextHeaders((name) => getCookieForApp(name));
    Object.entries(appContextHeaders).forEach(([key, value]) => {
      if (value && !config.headers[key]) {
        config.headers[key] = value;
      }
    });

    if (!config.headers['token']) {
      let token = getTokenForApp() || (await getToken());
      if (token) {
        config.headers['token'] = token;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

Requester.interceptors.response.use(
  (response) => {
    const endpoint = String(response.config?.url || '');
    const meta = response.data?.meta;
    if (meta && meta.statusCode !== 0 && meta.statusCode !== 104) {
      reportApiFailureFromRequester({
        endpoint,
        method: String(response.config?.method || '').toUpperCase(),
        httpStatus: response.status,
        statusCode: Number(meta.statusCode),
        message: String(meta.message || ''),
      });
    }
    return response;
  },
  async (error) => {
    if (error.response?.data?.meta?.statusCode === 104) {
      await logoutAndRenewToken();
      if (typeof window !== 'undefined') {
        endSession();
      }
      return {};
    }

    const endpoint = String(error.config?.url || '');
    reportApiFailureFromRequester({
      endpoint,
      method: String(error.config?.method || '').toUpperCase(),
      httpStatus: Number(error.response?.status || 0) || undefined,
      statusCode: Number(error.response?.data?.meta?.statusCode ?? -1),
      message: String(error.response?.data?.meta?.message || error.message || 'request failed'),
    });

    return Promise.reject(error);
  },
);

function stableStringify(value: any): string {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'object') return String(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

  const keys = Object.keys(value).sort();
  const entries = keys.map((k) => `${k}:${stableStringify(value[k])}`);
  return `{${entries.join(',')}}`;
}

function tokenHash(token: string): string {
  // Avoid putting raw tokens into cache keys.
  return crypto.createHash('sha256').update(token).digest('hex').slice(0, 12);
}

function hasHighCardinalityParams(params: any): boolean {
  if (!params || typeof params !== 'object') return false;

  // These keys commonly explode cache cardinality (filters/sort/search/pagination).
  const riskyKeys = new Set([
    'sort',
    'filter',
    'filters',
    'page',
    'sayfa',
    'rowsPerPage',
    'q',
    'term',
    'search',
    'query',
  ]);

  return Object.keys(params).some((k) => riskyKeys.has(k));
}

export async function fetcher<T>({
  endpoint,
  method = HTTP_REQUEST.GET,
  body = undefined,
  params = undefined,
  cacheEnabled = false,
  cacheKey = '',
  outsideToken = undefined,
  cacheRevalidateSeconds = 60,
  cacheScope = 'global',
  allowHighCardinalityCache = false,
  cacheTags = undefined,
  throwOnError = false,
  skipAuth = false,
}: {
  endpoint: string;
  method: HTTP_REQUEST;
  body?: any;
  params?: any;
  cacheEnabled: boolean;
  cacheKey?: string;
  outsideToken?: string;
  // Default: 60 seconds. Matches product-service catalog TTL so iOS/Android WebView
  // origin data also refreshes within a minute without waiting for panel Sync.
  cacheRevalidateSeconds?: number;
  // 'global' shares cache across tokens; 'perToken' isolates cache per token (safer but larger).
  cacheScope?: 'global' | 'perToken';
  // If true, allows caching requests that include sort/filter/search/pagination params.
  allowHighCardinalityCache?: boolean;
  // Optional override for Next cache tags.
  cacheTags?: string[];
  // Transport/HTTP failures throw instead of returning undefined. Empty 200 bodies
  // still resolve undefined (and are not written to Next cache).
  throwOnError?: boolean;
  // Public catalog (homepage CMS). Do not block SSR on /user/init.
  skipAuth?: boolean;
}): Promise<T> {
  let token = outsideToken || getTokenForApp() || (await getToken());
  if (!token && typeof window === 'undefined' && !skipAuth) {
    const { getServerGuestToken } = await import('./server/serverGuestToken');
    token = await getServerGuestToken();
  }

  const performRequest = async (requestToken: string) => {
    const appContextHeaders = buildAppContextHeaders((name) => getCookieForApp(name));
    const config = {
      params,
      headers: {
        ...(requestToken ? { token: requestToken } : {}),
        ...appContextHeaders,
      },
    };

    try {
      if (method === HTTP_REQUEST.GET) {
        const { data: result } = await Requester.get<GenericResponse<T>>(endpoint, config);
        return result.data;
      } else {
        const { data: result } = await Requester.post<GenericResponse<T>>(endpoint, body, config);
        return result.data;
      }
    } catch (e) {
      if (throwOnError) throw e;
      return undefined as T;
    }
  };

  let fetchData: (token: string) => Promise<T>;

  const paramsKey = stableStringify(params);

  // Prevent unbounded cache growth: do NOT cache high-cardinality requests unless explicitly allowed.
  const highCardinality = hasHighCardinalityParams(params);
  const effectiveCacheEnabled =
    cacheEnabled && !!cacheKey && (allowHighCardinalityCache || !highCardinality);

  // Optional token isolation for safety.
  const scopeKey = cacheScope === 'perToken' && token ? `t:${tokenHash(token)}` : 't:global';

  const useRedisL2 =
    typeof window === 'undefined' && cacheEnabled && !!cacheKey && cacheScope !== 'perToken';

  const slugHint =
    (params && typeof params === 'object' && params.slug && String(params.slug)) ||
    (cacheTags || []).find((t) => t.startsWith('slug:'))?.slice(5) ||
    (cacheKey.startsWith('slug:') ? cacheKey.slice(5) : undefined);

  const performWithL2 = async (requestToken: string) => {
    if (!useRedisL2) return performRequest(requestToken);
    const { withWebL2, WEB_DEFAULT_TTL_SECONDS, canonicalizeListCacheParams } = await import(
      '@/lib/server/webCatalogCache'
    );
    const rawParams = params && typeof params === 'object' ? params : {};
    const hasListParams = [
      'page',
      'sayfa',
      'rowsPerPage',
      'sort',
      'filter',
      'filters',
      'locationSlug',
    ].some(
      (key) => rawParams[key] !== undefined && rawParams[key] !== null && rawParams[key] !== '',
    );
    const listParams = hasListParams ? canonicalizeListCacheParams(rawParams) : undefined;
    return withWebL2({
      kind: cacheKey,
      id: cacheKey,
      params: listParams
        ? { ...listParams, extra: paramsKey }
        : { extra: paramsKey, scope: scopeKey },
      ttl: cacheRevalidateSeconds || WEB_DEFAULT_TTL_SECONDS,
      slug: slugHint,
      loader: () => performRequest(requestToken),
    });
  };

  if (effectiveCacheEnabled) {
    const baseTags = cacheTags && cacheTags.length ? [...cacheTags] : [cacheKey];
    const tags = baseTags.includes(REVALIDATE_TAG_ALL)
      ? baseTags
      : [...baseTags, REVALIDATE_TAG_ALL];

    fetchData = unstable_cache(
      async (t: string) => {
        const result = await performWithL2(t);

        if (result === undefined || result === null) {
          throw new Error('empty_response');
        }

        return result;
      },
      [cacheKey, scopeKey, paramsKey],
      {
        tags,
        revalidate: cacheRevalidateSeconds,
      },
    );
  } else {
    fetchData = useRedisL2 ? performWithL2 : performRequest;
  }

  try {
    return await fetchData(token || '');
  } catch (e) {
    // empty_response: successful upstream with nothing to cache. Not a transport error.
    if (e instanceof Error && e.message === 'empty_response') {
      return undefined as T;
    }
    if (throwOnError) throw e;
    // A transient empty/failed upstream response (thrown above to avoid caching it)
    // must not crash the whole Server Component render. Degrade gracefully by
    // returning undefined; callers already handle it (optional chaining / `|| []`),
    // same contract as the non-cached error path in performRequest.
    return undefined as T;
  }
}

export async function logoutAndRenewToken() {
  const session = await createAnonymousSession({
    osType: (typeof window !== 'undefined' ? getOsType() : undefined) || 'web',
  });
  const token = session?.token;
  if (typeof window !== 'undefined') {
    try {
      if (checkRPC('bridge')) {
        appRPC('bridge', 'logout', { reason: 'web_logout_or_token_refresh' });
      }
    } catch (e) {}
    setLocalUser(undefined);
    setIsAnonymous(true);
    clearFavorites();
    clearOrderFeedbackFlowStorage();
    if (token) {
      setTokenForApp(token);
      setLocalToken(token);
    }
    saveExperiments(session?.experiments || {}, { identityChanged: true });
  }
  return token;
}
